import classNames from 'clsx'
import { twMerge } from 'tailwind-merge'
import { ChangeEvent, ComponentProps, ReactNode } from 'react'
import { useFormContext, useController } from 'react-hook-form'

import { MessageError } from 'components/Form'

interface InputProps extends ComponentProps<'input'> {
  label?: ReactNode | string
  mask?: (value: string) => string
}

export const Input = ({
  name,
  mask = undefined,
  className = '',
  required = false,
  ...rest
}: InputProps) => {
  const { control } = useFormContext()
  const {
    field: { value, ...field },
    fieldState
  } = useController({ name, control, defaultValue: '' })

  const messageError = fieldState.error?.message
  const isError = typeof messageError === 'string'

  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>): void => {
    const formatValue = event.target.value.trimStart()

    if (mask) {
      field.onChange(mask(formatValue))
      return
    }

    field.onChange(formatValue)
  }

  return (
    <fieldset
      className={twMerge('flex flex-col gap-1 w-full items-start', className)}
    >
      {rest?.label && (
        <label
          htmlFor={name}
          className={classNames(
            'text-body2 font-regular',
            isError && 'text-error-500',
            rest?.disabled && 'text-font-color-disabled'
          )}
        >
          {rest.label}
          {required && <span className="text-error-500">*</span>}
        </label>
      )}
      <input
        type="text"
        maxLength={75}
        {...rest}
        {...field}
        name={name}
        value={value}
        onChange={handleChangeInput}
        className={classNames(
          'w-full bg-transparent h-10 px-4 rounded-lg placeholder:text-font-color-disabled text-body1 border border-gray-500/[.32] text-gray-800 outline-1 outline-primary disabled:cursor-not-allowed disabled:text-font-color-disabled hover:border-gray-800',
          isError &&
            'border-2 border-error-500 outline-transparent hover:border-error-500'
        )}
      />
      <MessageError className="mt-1">{messageError}</MessageError>
    </fieldset>
  )
}
